import type { Editor } from '@tiptap/react';

import type { IEditorQuery } from './../ricos-content-query';
import type { ModalService } from './../ricos-modals';
import type { PublicCommands, RicosServices } from './../ricos-types';

import EventEmitter from './lib/EventEmitter';
import type { ToolbarItem } from './ToolbarItemCreator';

export type ToolbarItemCreator = (
  content: RicosServices['content'],
  tiptapEditor: Editor,
  editorQuery: IEditorQuery,
  modalService: ModalService,
  publicCommands: PublicCommands
) => ToolbarItem;

type RicosToolbarProps = {
  toolbarItemCreators: ToolbarItemCreator[];
  content: RicosServices['content'];
  tiptapEditor: Editor;
  editorQuery: IEditorQuery;
  modalService: ModalService;
  publicCommands: PublicCommands;
};

export class RicosToolbar extends EventEmitter {
  static EVENTS = {
    UPDATED: 'UPDATED',
  };

  private tiptapEditor: Editor;

  toolbarItems: ToolbarItem[] = [];

  private toolbarItemCreators: ToolbarItemCreator[];

  private content: RicosServices['content'];

  private editorQuery: IEditorQuery;

  private modalService: ModalService;

  private publicCommands: PublicCommands;

  static create({
    toolbarItemCreators,
    content,
    tiptapEditor,
    editorQuery,
    modalService,
    publicCommands,
  }: RicosToolbarProps) {
    return new RicosToolbar({
      toolbarItemCreators,
      content,
      tiptapEditor,
      editorQuery,
      modalService,
      publicCommands,
    });
  }

  private constructor({
    toolbarItemCreators,
    content,
    tiptapEditor,
    editorQuery,
    modalService,
    publicCommands,
  }: RicosToolbarProps) {
    super();
    this.toolbarItems = [];
    this.toolbarItemCreators = toolbarItemCreators;
    this.content = content;
    this.tiptapEditor = tiptapEditor;
    this.editorQuery = editorQuery;
    this.modalService = modalService;
    this.publicCommands = publicCommands;
  }

  destroyToolbarItems() {
    this.toolbarItems.forEach(toolbarItem => {
      toolbarItem.destroy();
    });

    this.toolbarItems = [];
  }

  update() {
    this.toolbarItems.forEach(toolbarItem => {
      toolbarItem.updateAttributes();
    });
  }

  createToolbarItems() {
    this.destroyToolbarItems();

    this.toolbarItems = this.toolbarItemCreators.map(toolbarItemCreator => {
      const toolbarItem = toolbarItemCreator(
        this.content,
        this.tiptapEditor,
        this.editorQuery,
        this.modalService,
        this.publicCommands
      );

      toolbarItem.setup();

      return toolbarItem;
    });
  }

  shouldCreateToolbarItems() {
    return this.toolbarItems.length !== this.toolbarItemCreators.length;
  }

  getToolbarItemById(id: string) {
    return this.toolbarItems.find(item => item.id === id);
  }
}
